import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Card, CardContent,
} from '@mui/material';
import NummiusLoader from './NummiusLoader';
import LockIcon from '@mui/icons-material/Lock';
import useSubscriptionTier, { isFreeTierAppPath } from '../hooks/useSubscriptionTier';
import { QuizOfferCard } from './QuizPromo';

// Locked pages that are worth naming on the card ("RRG is part of Core").
const PAGE_NAMES = {
  '/app/rrg': 'Sector rotation (RRG)', '/app/market-overview': 'Market Overview', '/app/watchlists': 'Live watchlists',
  '/app/heatmap': 'The Heatmap', '/app/charting': 'Charting', '/app/analysis': 'Analysis', '/app/learning': 'The Learning center',
};

const REASON_MESSAGES = {
  trial_expired:   'Your 14-day free trial has ended.',
  past_due:        'Your payment is past due.',
  canceled:        'Your subscription was canceled.',
  blocked:         'Your account has been suspended.',
  no_subscription: 'No active subscription found.',
  paused:          'Your plan is paused. Resume it anytime in Billing.',
};

export default function SubscriptionGuard({ children }) {
  const { tier, reason } = useSubscriptionTier();
  const navigate = useNavigate();

  // Always allow billing through immediately, without waiting on the
  // tier fetch - it's also in FREE_TIER_APP_PATHS below once tier
  // resolves, this just skips the loading spinner for it specifically.
  if (window.location.pathname.startsWith('/app/billing')) return children;

  if (tier === 'loading') return <NummiusLoader variant="page" label="Checking access" />;

  if (tier === 'full') return children;

  // tier === 'free' (Duelist - no active paid subscription): allowed
  // through for its own curated page set, blocked (same card as always)
  // everywhere else in /app/*.
  if (isFreeTierAppPath(window.location.pathname)) return children;

  const message = REASON_MESSAGES[reason] || REASON_MESSAGES.no_subscription;

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh"
      sx={{ bgcolor: 'background.default', p: 3 }}>
      <Card sx={{ maxWidth: 480, width: '100%', textAlign: 'center', p: 2,
        border: '1px solid', borderColor: 'divider' }}>
        <CardContent>
          <Box sx={{ mb: 3 }}>
            <LockIcon sx={{ fontSize: 56, color: '#7c3aed', mb: 1 }} />
            <Typography variant="h5" fontWeight={700} mb={1}>
              Subscription Required
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={1}>
              {message}
            </Typography>
            {PAGE_NAMES[window.location.pathname] && (!reason || reason === 'no_subscription') && (
              <Typography variant="body2" color="text.secondary" mb={1}>
                {PAGE_NAMES[window.location.pathname]} is part of Core. Try it free for 14 days.
              </Typography>
            )}
            {reason === 'trial_expired' && (
              <Typography variant="body2" color="text.secondary">
                Your 14-day trial has ended. Choose a plan to keep access to all your data.
              </Typography>
            )}
          </Box>

          <QuizOfferCard placement="locked" />

          <Button variant="contained" size="large" fullWidth
            onClick={function() { navigate('/app/billing'); }}
            sx={{ mb: 2, bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' } }}>
            View Plans & Subscribe
          </Button>

          <Button variant="text" size="small" color="inherit"
            onClick={function() {
              localStorage.removeItem('token');
              localStorage.removeItem('userName');
              localStorage.removeItem('userEmail');
              localStorage.removeItem('userRole');
              navigate('/login');
            }}>
            Sign out
          </Button>

          <Typography variant="caption" display="block" mt={2} color="text.secondary">
            Your data is safe. It will be here when you return.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
